import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import PublicLayout from './layout/PublicLayout';
import PrivateLayout from './layout/PrivateLayout';
import ProtectedRoute from './components/ProtectedRoute';
import PublicRoute from './components/PublicRoute';
import { routes } from './routes';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import { useAppDispatch, useAppSelector } from './store/hooks';
import { initializeUser, fetchUserProfile } from './store/slices/userSlice';
import { cookieUtils } from './utils/cookies';
import './App.css';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const App: React.FC = () => {
  const dispatch = useAppDispatch();
  const { user, isAuthenticated } = useAppSelector((state) => state.user);

  useEffect(() => {
    // Initialize user from cookies on mount
    dispatch(initializeUser());

    // If user is already logged in (has token), fetch fresh profile from API
    const token = cookieUtils.getAccessToken();
    if (token) {
      dispatch(fetchUserProfile());
    }
  }, [dispatch]);

  // Convert ApiUser to User format for components that need it
  const defaultUser = user ? {
    id: user.id,
    name: user.name || '',
    email: user.email || '',
    streak: 0,
    xp: 0,
    sessions: 0,
    createdAt: new Date(user.createdAt),
    updatedAt: new Date(user.updatedAt)
  } : {
    id: '',
    name: '',
    email: '',
    streak: 0,
    xp: 0,
    sessions: 0,
    createdAt: new Date(),
    updatedAt: new Date()
  };

  return (
    <Router>
      <div className="App">
        <Routes>
          {routes.map((route) => {
            const Component = route.component;
            return (
              <Route
                key={route.path}
                path={route.path}
                element={
                  route.requiresAuth ? (
                    <ProtectedRoute>
                      <PrivateLayout hideHeader={route.hideHeader}>
                        <Component />
                      </PrivateLayout>
                    </ProtectedRoute>
                  ) : (
                    <PublicRoute isAuthRoute={route.isAuthRoute}>
                      <PublicLayout>
                        <Component />
                      </PublicLayout>
                    </PublicRoute>
                  )
                }
              />
            );
          })}
          {/* Catch-all route to redirect undefined paths to dashboard */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
        <PWAInstallPrompt />
        <ToastContainer position="bottom-left" stacked />
      </div>
    </Router>
  );
};

export default App;